"use client";

import { isLandauLive } from "@/lib/consts";
import { getVisibleExpertiseData } from "@/lib/expertiseData";
import type { Locale } from "@/lib/i18n/locales";
import { slRoutes, solvingTaxSlug } from "@/lib/slRoutes";
import { useMessages } from "@/lib/i18n/useMessages";
import {
  getTeamMemberSlug,
  getTeamMembersRecord,
  TeamMemberEnum,
} from "@/lib/teamData";
import { SLLink } from "@/components/i18n/SLLink";
import { SLHtmlFromLangFile } from "@/components/sections/home/SLHtmlFromLangFile";
import {
  Box,
  Divider,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";

const solvingTaxName = "Solving Tax";

export function SLNavigationFlyoutMenus({
  pathname,
  locale,
  anchorElExpertise,
  onCloseExpertiseMenu,
  anchorElAboutUs,
  onCloseAboutUsMenu,
}: {
  pathname: string;
  locale: Locale;
  anchorElExpertise: null | HTMLElement;
  onCloseExpertiseMenu: () => void;
  anchorElAboutUs: null | HTMLElement;
  onCloseAboutUsMenu: () => void;
}) {
  const theme = useTheme();
  const messages = useMessages();
  const teamRecord = getTeamMembersRecord(messages.team);
  const moritzSlug = getTeamMemberSlug(TeamMemberEnum.MoritzRiehl);
  const paperSx = {
    backgroundColor: theme.palette.background.default,
  };

  return (
    <>
      <Menu
        id="expertise-menu"
        aria-labelledby="expertise-button"
        anchorEl={anchorElExpertise}
        open={Boolean(anchorElExpertise)}
        onClose={onCloseExpertiseMenu}
        disableScrollLock
        slotProps={{
          paper: { sx: paperSx },
        }}
      >
        {Object.values(getVisibleExpertiseData(locale)).map((item) => {
          const isInView = pathname.startsWith(
            slRoutes.expertiseField(item.slug),
          );

          return (
            <MenuItem
              sx={{
                px: 3,
                py: 2,
                textDecoration: isInView ? "underline" : "none",
                textUnderlineOffset: "8px",
              }}
              key={item.slug}
              component={SLLink}
              href={slRoutes.expertiseField(item.slug)}
            >
              <SLHtmlFromLangFile htmlString={item.title} component="span" />
            </MenuItem>
          );
        })}
      </Menu>

      <Menu
        id="about-us-menu"
        aria-labelledby="about-button"
        anchorEl={anchorElAboutUs}
        open={Boolean(anchorElAboutUs)}
        onClose={onCloseAboutUsMenu}
        disableScrollLock
        slotProps={{
          paper: { sx: paperSx },
        }}
      >
        <Typography sx={{ ml: 2 }} variant="overline">
          Solving Legal
        </Typography>
        {[
          ...Object.values(teamRecord),
          {
            slug: solvingTaxSlug,
            fullName: solvingTaxName,
            role: messages.common.navAboutMenu.solvingTaxRole,
          },
        ]
          .filter((member) =>
            isLandauLive ? true : member.slug !== moritzSlug,
          )
          .map((member) => {
            const isInView =
              pathname.startsWith(slRoutes.teamMember(member.slug)) ||
              (member.fullName === solvingTaxName &&
                pathname.startsWith(slRoutes.solvingTax));

            return (
              <Box key={member.fullName}>
                {member.fullName === solvingTaxName && (
                  <>
                    <Divider />
                    <Typography sx={{ ml: 2 }} variant="overline">
                      Solving Tax
                    </Typography>
                  </>
                )}
                <MenuItem
                  sx={{
                    px: 3,
                    py: 2,
                    textDecoration: isInView ? "underline" : "none",
                    textUnderlineOffset: "8px",
                  }}
                  key={member.slug}
                  component={SLLink}
                  href={slRoutes.teamMember(member.slug)}
                >
                  <SLHtmlFromLangFile
                    htmlString={member.fullName}
                    component="span"
                  />
                </MenuItem>
              </Box>
            );
          })}
      </Menu>
    </>
  );
}
